import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KanbanBoard } from "../../../../components/ui/KanbanBoard";
import { updateTask } from "../../../../services/tasksEndpoints";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import type { TaskRecord, BoardStatus } from "../tasksTypes";
import {
  ALL_BOARD_COLUMN_NAMES,
  BOARD_COLUMN_COLORS,
  BOARD_STATUS_LABELS,
  BOARD_STATUS_ORDER,
} from "../tasksTypes";
import TaskCard from "./TaskCard";

type TasksBoardProps = {
  tasks: TaskRecord[];
  onTaskClick?: (task: TaskRecord) => void;
  members?: any[];
  memberLookup?: Record<string, { name: string; email: string; initials: string }>;
};

const labelToStatus: Record<string, BoardStatus> = Object.fromEntries(
  BOARD_STATUS_ORDER.map((status) => [BOARD_STATUS_LABELS[status], status]),
) as Record<string, BoardStatus>;

const TasksBoard = ({ tasks, onTaskClick, members = [], memberLookup = {} }: TasksBoardProps) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: async ({
      taskExternalId,
      newStatus,
    }: {
      taskExternalId: string;
      newStatus: BoardStatus;
    }) => {
      if (!workspace?.id) throw new Error("No workspace");
      return updateTask({
        workspace_id: workspace.id,
        task_external_id: taskExternalId,
        updates: { board_status: newStatus },
      });
    },
    onMutate: async ({ taskExternalId, newStatus }) => {
      // Cancel outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: ["tasks-page", workspace?.id] });

      // Snapshot previous value
      const previousTasks = queryClient.getQueryData<TaskRecord[]>(["tasks-page", workspace?.id]);

      // Optimistically update the task's board_status
      queryClient.setQueryData<TaskRecord[]>(["tasks-page", workspace?.id], (old) =>
        (old || []).map((t) =>
          t.external_id === taskExternalId ? { ...t, board_status: newStatus } : t,
        ),
      );

      return { previousTasks };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks-page", workspace?.id] });
      queryClient.invalidateQueries({ queryKey: ["task-board-counts", workspace?.id] });
    },
    onError: (error, variables, context) => {
      // Revert optimistic update
      if (context?.previousTasks) {
        queryClient.setQueryData(["tasks-page", workspace?.id], context.previousTasks);
      }

      console.error("[TasksBoard] Failed to update task status:", {
        taskExternalId: variables.taskExternalId,
        newStatus: variables.newStatus,
        error,
      });

      notify({
        heading: "Failed to update task",
        type: "failure",
        messageList: ["Could not move the task. Please try again."],
      });
    },
  });

  const handleDragEnd = (itemId: string, _fromColumn: string, toColumn: string) => {
    const newStatus = labelToStatus[toColumn];
    if (!newStatus) return;

    const task = tasks.find((t) => t.id === itemId);
    if (!task) return;

    updateMutation.mutate({
      taskExternalId: task.external_id,
      newStatus,
    });
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <KanbanBoard<TaskRecord>
        items={tasks}
        groupByField="board_status"
        getGroupValue={(task) => BOARD_STATUS_LABELS[task.board_status] || "Investigating"}
        columns={ALL_BOARD_COLUMN_NAMES}
        columnColors={BOARD_COLUMN_COLORS}
        renderCard={(task) => <TaskCard task={task} onClick={onTaskClick} members={members} memberLookup={memberLookup} />}
        onDragEnd={handleDragEnd}
        showEmptyColumns
      />
    </div>
  );
};

export default TasksBoard;
